import Phaser from 'phaser';
import type { MapData } from '@shared/types/map';
import {
  GAME_FONT_FAMILY,
  WORLD_TILES,
  VIEW_HALF_RANGE_X,
  VIEW_HALF_RANGE_Y,
  TILE_SIZE,
} from '@shared/constants';
import { getSurfaceById, getSurfaceTextureKey } from '../../sprites/SurfaceSpriteManager';
import { loadMarkers, saveMarkers, newMarkerId, type MapMarker } from './MapMarkers';
import { showPromptModal } from './MapPromptModal';
import { Direction } from '@shared/types/entity';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { bakeMapTexture } from './minimapBake';
import type { QuestView } from '@shared/types/quest';
import {
  buildWorldMapIndex,
  resolveObjectiveViewTargets,
  nearestTarget,
  type WorldMapIndex,
  type WorldMapNpc,
  type HostileCount,
  type WorldMapResourceNode,
} from '@shared/utils/worldMapIndex';
import { getItemTemplate } from '@shared/data/items';
import { findCoarseRoute } from '../../util/pathfinding';
import { WorldMapDock, DEFAULT_FILTERS, type WorldMapFilters } from './worldMap/WorldMapDock';
import { NpcDetailCard } from './worldMap/NpcDetailCard';

/** Colores por tipo de zona (coinciden con la legenda del dock / AREAS.md). */
const ZONE_FILL: Record<string, number> = {
  safe: 0x5fbf6a,
  neutral: 0xc9b45f,
  especial: 0xd6a83f,
  danger: 0xd6564a,
};
/** Zoom mínimo para mostrar pins individuales de NPC neutral (disclosure). */
const NPC_PIN_ZOOM = 2.2;

/**
 * Overlay fullscreen del world map. Se abre con `M` (default) y muestra
 * todo el mapa actual a escala, con un marker "you are here" pulsante.
 *
 * Renderizado:
 * - El mapa se hornea UNA vez en una textura (sprites REALES de suelo/caminos/
 *   árboles/agua/entradas) a resolución adaptativa según la GPU (pxPerTile px por
 *   tile, hasta 8192 de lado) y se cachea por id de mapa. Es una sola imagen, así
 *   que abrir/cerrar/zoom no recrea nada (no se cuelga con ~66k tiles).
 * - Al abrir, esa textura se escala para encajar en ~85% del menor lado de la
 *   pantalla; toda la matemática tile↔pantalla usa screenPxPerTile().
 * - No se renderizan entities (decisión del usuario: solo terreno).
 */

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

// Paleta del minimapa.
// Colores de terreno: ahora viven en ./minimapColors (compartidos con el minimapa in-game).
const COLOR_OVERLAY_BG = 0x000000;
const OVERLAY_ALPHA = 0.85;
const COLOR_VISION = 0x88ddff;
const DEFAULT_MARKER_COLOR = 0xffd400;

// Límites de zoom (multiplicador sobre baseScale = fit-to-screen).
const MIN_ZOOM = 1.0;
const MAX_ZOOM = 10.0;
const ZOOM_FACTOR = 1.2;

// Fundido suave de apertura/cierre del world map (ms).
const FADE_MS = 200;
/** Cualquier objeto con alfa animable (todo lo que el overlay puede desvanecer). */
interface FadeGO {
  alpha: number;
  setAlpha(value?: number): unknown;
}

// Anclaje de cabeza/casco del mini-marker del player, replicando GameScene
// (HEAD_OFFSET_FROM_BODY_BOTTOM=27, HEAD_FRAME_HEIGHT=30): el container tiene el
// origen en los pies (y=0), la cabeza se centra a 27+15=42px por encima — NO al
// tope del frame del body (eso la dejaba flotando despegada).
const HEAD_FRAME_HEIGHT = 30;
const HEAD_OFFSET_FROM_BODY_BOTTOM = 27;
const HEAD_CENTER_Y = -(HEAD_OFFSET_FROM_BODY_BOTTOM + HEAD_FRAME_HEIGHT / 2); // -42
const HELM_CENTER_Y = HEAD_CENTER_Y - 8; // casco 8px sobre el centro de la cabeza

export interface LocalPlayerVisual {
  bodyId: number;
  headId: number;
  helmId?: number;
  weaponId?: number;
  shieldId?: number;
  bootId?: number;
  direction: Direction;
}

export interface MapOverlayOptions {
  scene: Phaser.Scene;
  /** MapData del mapa actual a renderizar. */
  mapData: MapData;
  /** Callback que devuelve la posición del local player en tiles. */
  getLocalPlayerTile: () => { x: number; y: number } | null;
  /** Callback que devuelve la visual completa del local player (body, head, equipo). */
  getLocalPlayerVisual: () => LocalPlayerVisual | null;
  /** Callback que devuelve el characterId actual (para persistir markers por character). */
  getCharacterId: () => string | null;
  /** Depth base del overlay (debe estar arriba de todo). */
  depth?: number;
  /** True si el player local es admin (gatea el teleport por click derecho). */
  isAdmin?: () => boolean;
  /** Admin: click derecho sobre un tile del mapa → teleport a (tileX, tileY). */
  onAdminTeleport?: (tileX: number, tileY: number) => void;
  /** Quests activas del player (para el tab Quests + ruta). Opcional. */
  getActiveQuests?: () => QuestView[];
  /** IDs de quests ya entregadas (para el catálogo del tab Quests). Opcional. */
  getCompletedQuestIds?: () => ReadonlySet<string>;
  /** Nivel del player local (para gatear por color el catálogo de quests). Opcional. */
  getPlayerLevel?: () => number;
  /** Set de tiles bloqueados de TODO el mapa (para el pathfinding de rutas). */
  getBlockedTiles?: () => Set<string> | null;
  /** Set de tiles de agua navegable (bloqueo para la ruta terrestre). */
  getWaterTiles?: () => Set<string> | null;
  /** Quest favorita/trackeada actual (fuente única = QuestUI). Inicializa la ruta al abrir. */
  getTrackedQuestId?: () => string | null;
  /** Notifica cuando cambia la quest trackeada desde el dock (para sincronizar la favorita). */
  onTrackQuestChange?: (questId: string | null) => void;
  /** Notifica visibilidad del overlay (para ocultar el panel flotante de quests mientras está abierto). */
  onVisibilityChange?: (visible: boolean) => void;
}

export class MapOverlay {
  private readonly scene: Phaser.Scene;
  private readonly mapData: MapData;
  private readonly getLocalPlayerTile: () => { x: number; y: number } | null;
  private readonly getLocalPlayerVisual: () => LocalPlayerVisual | null;
  private readonly getCharacterId: () => string | null;
  private readonly depth: number;
  private readonly isAdmin: (() => boolean) | undefined;
  private readonly onAdminTeleport: ((tileX: number, tileY: number) => void) | undefined;
  private readonly getActiveQuests: (() => QuestView[]) | undefined;
  private readonly getCompletedQuestIds: (() => ReadonlySet<string>) | undefined;
  private readonly getPlayerLevel: (() => number) | undefined;
  private readonly getBlockedTiles: (() => Set<string> | null) | undefined;
  private readonly getWaterTiles: (() => Set<string> | null) | undefined;
  private readonly getTrackedQuestId: (() => string | null) | undefined;
  private readonly onTrackQuestChange: ((questId: string | null) => void) | undefined;
  private readonly onVisibilityChange: ((visible: boolean) => void) | undefined;

  // === World Map interactivo (índice + capas + dock + tarjeta) ===
  private readonly wmIndex: WorldMapIndex;
  private dock: WorldMapDock | null = null;
  private npcCard: NpcDetailCard | null = null;
  private filters: WorldMapFilters = { ...DEFAULT_FILTERS };
  /** Overlay de áreas (rects + labels). */
  private areaGfx: Phaser.GameObjects.Graphics | null = null;
  private areaLabels: Phaser.GameObjects.Text[] = [];
  private highlightedAreaId: string | null = null;
  /** Pins de NPCs neutrales (creados al abrir, reposicionados por transform). */
  private neutralPins: Array<{ npc: WorldMapNpc; dot: Phaser.GameObjects.Arc }> = [];
  /** Badges de conteo de hostiles (por área con nombre + clusters de mundo abierto). */
  private hostileBadges: Array<{
    tileX: number;
    tileY: number;
    total: number;
    breakdown: HostileCount[];
    cont: Phaser.GameObjects.Container;
  }> = [];
  /** Estrellas de boss (hostiles isBoss) en su placement. */
  private bossPins: Array<{ npc: WorldMapNpc; star: Phaser.GameObjects.Text }> = [];
  /** Pins de yacimientos (minería/leñería) en sus posiciones fijas. */
  private resourcePins: Array<{ node: WorldMapResourceNode; marker: Phaser.GameObjects.Rectangle }> = [];
  /** Ruta de quest (breadcrumb) + destino. */
  private routeGfx: Phaser.GameObjects.Graphics | null = null;
  private routeDest: Phaser.GameObjects.Arc | null = null;
  private routeLabel: Phaser.GameObjects.Text | null = null;
  private trackedQuestId: string | null = null;
  /** Waypoints (tiles) de la ruta cacheada + firma para recomputar. */
  private routeTiles: Array<{ x: number; y: number }> | null = null;
  private routeSig = '';
  /** Destino de la ruta (tile + etiqueta) para el marker pulsante. */
  private routeGoal: { x: number; y: number; label: string } | null = null;
  /** Popover de desglose de hostiles de una zona (Phaser). */
  private hostilePopover: Phaser.GameObjects.Container | null = null;

  private visible = false;
  // Fundido de apertura/cierre: tween activo, flag de "cerrando" y alfa base
  // recordado por objeto (para restaurar el look diseñado, no aplanar todo a 1).
  private fadeCounter: Phaser.Tweens.Tween | null = null;
  private fadingOut = false;
  private readonly fadeBase = new Map<FadeGO, number>();
  private bg: Phaser.GameObjects.Rectangle | null = null;
  private mapImage: Phaser.GameObjects.Image | null = null;
  // Marker del local player = mini render del personaje (body + head + equipo).
  private playerContainer: Phaser.GameObjects.Container | null = null;
  private playerBody: Phaser.GameObjects.Sprite | null = null;
  private playerHead: Phaser.GameObjects.Sprite | null = null;
  private playerHelm: Phaser.GameObjects.Sprite | null = null;
  private playerWeapon: Phaser.GameObjects.Sprite | null = null;
  private playerShield: Phaser.GameObjects.Sprite | null = null;
  private playerBoot: Phaser.GameObjects.Sprite | null = null;
  private lastPlayerVisual: LocalPlayerVisual | null = null;
  private visionRect: Phaser.GameObjects.Rectangle | null = null;
  private title: Phaser.GameObjects.Text | null = null;
  private hint: Phaser.GameObjects.Text | null = null;
  private cachedTextureKey: string | null = null;
  private markerPulse: Phaser.Tweens.Tween | null = null;
  /** Píxeles de textura por tile en el mapa horneado (resolución adaptativa). */
  private pxPerTile = 1;
  /** Lado de la textura horneada en px (= WORLD_TILES * pxPerTile). */
  private texSize = WORLD_TILES;

  // Pan / drag state.
  private isDragging = false;
  private dragStartPointerX = 0;
  private dragStartPointerY = 0;
  private dragStartOffsetX = 0;
  private dragStartOffsetY = 0;

  // Sprites reales de los tiles del mapa (decoraciones / surfaces).
  // Se posicionan/escalan en applyTransform junto al mapImage base.
  private decorationSprites: Phaser.GameObjects.Image[] = [];

  // === Marcadores del usuario ===
  private markers: MapMarker[] = [];
  private markerDots: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private markerLabels: Map<string, Phaser.GameObjects.Text> = new Map();
  // La LISTA de marcadores (crear/renombrar/borrar/ir a) vive en el dock derecho
  // (WorldMapDock, sección inferior). Acá sólo quedan los dots/labels que se ven en el mapa.
  // Modo "click adentro del mapa para crear marker".
  private placingMarker = false;
  private boundPlaceEscHandler: ((e: KeyboardEvent) => void) | null = null;
  // Highlight del tile bajo el cursor durante el modo placement.
  private placementHighlight: Phaser.GameObjects.Rectangle | null = null;

  // Garantiza que el primer open de la sesión arranque centrado en el player al MAX_ZOOM.
  // Se mantiene en memoria — no en localStorage — para que sólo aplique al primer toggle.
  private hasOpenedThisSession = false;

  // Transform persistente entre toggles (se mantiene durante la sesión).
  // Sólo se resetea al middle-click.
  private zoom = 1;
  private offsetX = 0;
  private offsetY = 0;

  // Wheel handler bound a esta instancia (para poder hacer off correctamente).
  private boundWheelHandler:
    | ((p: Phaser.Input.Pointer, gos: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void)
    | null = null;

  constructor(opts: MapOverlayOptions) {
    this.scene = opts.scene;
    this.mapData = opts.mapData;
    this.getLocalPlayerTile = opts.getLocalPlayerTile;
    this.getLocalPlayerVisual = opts.getLocalPlayerVisual;
    this.getCharacterId = opts.getCharacterId;
    this.depth = opts.depth ?? 1500; // arriba del HUD y de todo lo demás
    this.isAdmin = opts.isAdmin;
    this.onAdminTeleport = opts.onAdminTeleport;
    this.getActiveQuests = opts.getActiveQuests;
    this.getCompletedQuestIds = opts.getCompletedQuestIds;
    this.getPlayerLevel = opts.getPlayerLevel;
    this.getBlockedTiles = opts.getBlockedTiles;
    this.getWaterTiles = opts.getWaterTiles;
    this.getTrackedQuestId = opts.getTrackedQuestId;
    this.onTrackQuestChange = opts.onTrackQuestChange;
    this.onVisibilityChange = opts.onVisibilityChange;
    // Índice del mapa (áreas/ciudades/NPCs/drops), memoizado por id de mapa.
    this.wmIndex = buildWorldMapIndex(this.mapData);
  }

  toggle(): void {
    // Mientras se está cerrando (fade-out), M reabre revirtiendo el fundido.
    if (this.visible && !this.fadingOut) this.hide();
    else this.show();
  }

  show(): void {
    // Reabrir en pleno fade-out: revertir el fundido sin reconstruir nada.
    if (this.fadingOut) {
      this.fadingOut = false;
      this.dock?.fadeTo(1, FADE_MS);
      this.runFade((base) => base);
      this.onVisibilityChange?.(true);
      return;
    }
    if (this.visible) return;
    if (!this.cachedTextureKey) this.generateMapTexture();
    this.buildUI();
    // El terreno (suelo + decoraciones) se hornea en la textura del minimapa
    // (generateMapTexture); no se crean sprites por tile (ver perf más abajo).
    // Cargar marcadores del personaje actual + dots en el mapa. La LISTA de marcadores
    // vive en el dock derecho (sección inferior), no en un panel Phaser aparte.
    this.loadMarkersForCharacter();
    this.buildMarkerDots();
    // Fuente única de la quest trackeada: la favorita de QuestUI. Así la ruta
    // aparece sola al abrir si hay una favorita, sin tocar el dock.
    this.trackedQuestId = this.getTrackedQuestId?.() ?? null;
    // Capas del World Map interactivo + dock derecho + tarjeta de NPC.
    this.buildWorldMapLayers();
    this.buildDock();
    // Primer open de la sesión: centrado en el player al MAX_ZOOM.
    // Luego persiste lo que el usuario dejó (zoom/offset).
    if (!this.hasOpenedThisSession) {
      const playerTile = this.getLocalPlayerTile();
      if (playerTile) {
        const { width: screenW, height: screenH } = this.scene.scale;
        this.zoom = MAX_ZOOM;
        const scale = this.getBaseScale() * MAX_ZOOM;
        const mapW = WORLD_TILES * scale;
        const mapH = WORLD_TILES * scale;
        const baseMapX = (screenW - mapW) / 2;
        const baseMapY = (screenH - mapH) / 2;
        const desiredMapX = screenW / 2 - (playerTile.x + 0.5) * scale;
        const desiredMapY = screenH / 2 - (playerTile.y + 0.5) * scale;
        this.offsetX = desiredMapX - baseMapX;
        this.offsetY = desiredMapY - baseMapY;
      }
      this.hasOpenedThisSession = true;
    }
    // Aplicar zoom/offset recordados (persisten entre toggles).
    this.applyTransform();
    // Registrar wheel handler en scene.input (firma correcta de 4 args).
    this.boundWheelHandler = (pointer, _gameObjects, _dx, dy) => {
      this.handleWheelZoom(pointer, dy);
    };
    this.scene.input.on('wheel', this.boundWheelHandler);
    // ESC cancela el modo place-marker (sin cerrar el mapa).
    this.boundPlaceEscHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.placingMarker) {
        this.exitPlacingMode();
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', this.boundPlaceEscHandler, true);
    this.visible = true;
    // Fundido de entrada: recordar el alfa base de cada objeto, arrancarlos en 0
    // y animarlos hasta su base. El dock DOM entra por transición CSS.
    this.fadeBase.clear();
    for (const o of this.collectFadeTargets()) {
      this.fadeBase.set(o, o.alpha);
      o.setAlpha(0);
    }
    this.dock?.fadeTo(1, FADE_MS);
    this.runFade((base) => base);
    this.onVisibilityChange?.(true);
  }

  hide(): void {
    if (!this.visible || this.fadingOut) return;
    // Fundido de salida: animar todo a 0 y recién ahí destruir (teardown).
    this.fadingOut = true;
    this.dock?.fadeTo(0, FADE_MS);
    this.runFade(() => 0, () => this.teardown());
  }

  /**
   * Destrucción inmediata (sin fundido), para cleanup programático — p.ej. recrear
   * el overlay al recargar de mapa. Corta cualquier fade en curso.
   */
  destroy(): void {
    this.fadeCounter?.remove();
    this.fadeCounter = null;
    this.fadingOut = false;
    if (this.visible) this.teardown();
  }

  /** Destrucción real del overlay (corre al terminar el fade-out). */
  private teardown(): void {
    this.markerPulse?.stop();
    this.markerPulse = null;
    if (this.boundWheelHandler) {
      this.scene.input.off('wheel', this.boundWheelHandler);
      this.boundWheelHandler = null;
    }
    this.destroyDecorationSprites();
    this.destroyMarkerDots();
    this.destroyWorldMapLayers();
    if (this.boundPlaceEscHandler) {
      window.removeEventListener('keydown', this.boundPlaceEscHandler, true);
      this.boundPlaceEscHandler = null;
    }
    if (this.placingMarker) this.exitPlacingMode();
    this.bg?.destroy(); this.bg = null;
    this.mapImage?.destroy(); this.mapImage = null;
    // El container destruye automáticamente sus children sprites.
    this.playerContainer?.destroy(); this.playerContainer = null;
    this.playerBody = null;
    this.playerHead = null;
    this.playerHelm = null;
    this.playerWeapon = null;
    this.playerShield = null;
    this.playerBoot = null;
    this.lastPlayerVisual = null;
    this.visionRect?.destroy(); this.visionRect = null;
    this.title?.destroy(); this.title = null;
    this.hint?.destroy(); this.hint = null;
    this.fadeCounter = null;
    this.fadeBase.clear();
    this.fadingOut = false;
    this.visible = false;
    this.onVisibilityChange?.(false);
  }

  isVisible(): boolean {
    return this.visible;
  }

  // === FUNDIDO (fade in/out) ===============================================

  /**
   * Reúne TODOS los objetos Phaser que el overlay tiene vivos ahora mismo, para
   * animar su alfa en bloque. Los popovers/tarjeta dinámicos aparecen recién tras
   * el fade-in, así que no hace falta incluirlos.
   */
  private collectFadeTargets(): FadeGO[] {
    const out: FadeGO[] = [];
    const push = (o: FadeGO | null | undefined): void => { if (o) out.push(o); };
    push(this.bg);
    push(this.mapImage);
    push(this.visionRect);
    push(this.playerContainer);
    push(this.title);
    push(this.hint);
    for (const d of this.markerDots.values()) push(d);
    for (const l of this.markerLabels.values()) push(l);
    push(this.areaGfx);
    for (const l of this.areaLabels) push(l);
    for (const p of this.neutralPins) push(p.dot);
    for (const b of this.hostileBadges) push(b.cont);
    for (const b of this.bossPins) push(b.star);
    for (const r of this.resourcePins) push(r.marker);
    push(this.routeGfx);
    push(this.routeDest);
    push(this.routeLabel);
    return out;
  }

  /**
   * Anima el alfa de todos los targets desde su alfa actual hasta `target(base)`,
   * donde `base` es el alfa diseñado del objeto (recordado en `fadeBase`). Cerrando
   * → `() => 0`; abriendo/reabriendo → `(base) => base`.
   */
  private runFade(target: (base: number) => number, onComplete?: () => void): void {
    this.fadeCounter?.remove();
    const items = this.collectFadeTargets().map((obj) => ({
      obj,
      from: obj.alpha,
      to: target(this.fadeBase.get(obj) ?? obj.alpha),
    }));
    this.fadeCounter = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: FADE_MS,
      ease: 'Sine.easeInOut',
      onUpdate: (tw) => {
        const p = tw.getValue() ?? 1;
        for (const it of items) it.obj.setAlpha(it.from + (it.to - it.from) * p);
      },
      onComplete: () => {
        for (const it of items) it.obj.setAlpha(it.to);
        this.fadeCounter = null;
        onComplete?.();
      },
    });
  }

  /**
   * Tick por frame para actualizar la posición del marker mientras el
   * player se mueve con el mapa abierto.
   */
  update(): void {
    if (!this.visible) return;
    this.refreshPlayerMarker();
    this.refreshVisionRect();
    // Mientras el player camina con el mapa abierto, reubicar pins/ruta y
    // recomputar la ruta si hizo falta (el recompute está gateado por firma).
    this.refreshWorldMapLayers();
  }

  // === INTERNALS ============================================================

  /**
   * Pre-hornea la textura del mapa (si falta), fuera del primer open para evitar
   * el hitch. Llamado por GameScene tras cargar el mapa y sus assets.
   */
  prebakeTexture(): void {
    if (!this.cachedTextureKey) this.generateMapTexture();
  }

  /**
   * Genera (o reusa) la textura del mapa con sprites REALES. Delega al helper compartido
   * `bakeMapTexture`, que también usa el minimapa in-game (misma textura cacheada por id).
   */
  private generateMapTexture(): void {
    const baked = bakeMapTexture(this.scene, this.mapData);
    this.cachedTextureKey = baked.key;
    this.pxPerTile = baked.pxPerTile;
    this.texSize = baked.texSize;
  }

  private buildUI(): void {
    const { width: screenW, height: screenH } = this.scene.scale;

    // Fondo semi-transparente full-screen — atenúa el HUD y captura clicks.
    this.bg = this.scene.add
      .rectangle(0, 0, screenW, screenH, COLOR_OVERLAY_BG, OVERLAY_ALPHA)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(this.depth)
      .setInteractive({ useHandCursor: false });

    // Comportamiento del bg:
    //  - Middle-click anywhere → reset zoom + offset al default.
    //  - Click AFUERA del mapa → cierra el overlay.
    //  - Click + drag ADENTRO del mapa → pan (1:1 en screen pixels, naturalmente
    //    proporcional al zoom: a mayor zoom, drag mueve menos tiles del mundo).
    this.bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Admin: click derecho sobre un tile del mapa → teleport ahí.
      if (pointer.rightButtonDown() && this.mapImage && this.isAdmin?.()) {
        const mx = this.mapImage.x;
        const my = this.mapImage.y;
        const scale = this.screenPxPerTile();
        const mw = WORLD_TILES * scale;
        const mh = WORLD_TILES * scale;
        const inside = pointer.x >= mx && pointer.x <= mx + mw
                    && pointer.y >= my && pointer.y <= my + mh;
        if (inside) {
          const tileX = Math.floor((pointer.x - mx) / scale);
          const tileY = Math.floor((pointer.y - my) / scale);
          this.onAdminTeleport?.(tileX, tileY);
          this.hide(); // cerrar el mapa tras teleportar (se siente natural)
          pointer.event.stopPropagation();
          return;
        }
      }
      if (pointer.middleButtonDown()) {
        this.zoom = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.applyTransform();
        pointer.event.stopPropagation();
        return;
      }
      if (this.mapImage) {
        const mx = this.mapImage.x;
        const my = this.mapImage.y;
        const scale = this.screenPxPerTile();
        const mw = WORLD_TILES * scale;
        const mh = WORLD_TILES * scale;
        const outside = pointer.x < mx || pointer.x > mx + mw
                     || pointer.y < my || pointer.y > my + mh;
        if (outside) {
          // Si estamos en modo place-marker, cancelarlo en vez de cerrar.
          if (this.placingMarker) {
            this.exitPlacingMode();
            pointer.event.stopPropagation();
            return;
          }
          this.hide();
          pointer.event.stopPropagation();
          return;
        }
        // Adentro del mapa:
        if (this.placingMarker) {
          // Crear marker en el tile clickeado.
          const tileX = Math.floor((pointer.x - mx) / scale);
          const tileY = Math.floor((pointer.y - my) / scale);
          this.createMarkerAt(tileX, tileY);
          this.exitPlacingMode();
          pointer.event.stopPropagation();
          return;
        }
        // Drag/pan normal.
        this.isDragging = true;
        this.dragStartPointerX = pointer.x;
        this.dragStartPointerY = pointer.y;
        this.dragStartOffsetX = this.offsetX;
        this.dragStartOffsetY = this.offsetY;
      }
      pointer.event.stopPropagation();
    });

    this.bg.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Hover highlight durante create-marker: muestra cuadrado bajo el cursor.
      if (this.placingMarker && this.placementHighlight && this.mapImage) {
        const mx = this.mapImage.x;
        const my = this.mapImage.y;
        const scale = this.screenPxPerTile();
        const mw = WORLD_TILES * scale;
        const mh = WORLD_TILES * scale;
        const outside = pointer.x < mx || pointer.x > mx + mw
                     || pointer.y < my || pointer.y > my + mh;
        if (outside) {
          this.placementHighlight.setVisible(false);
        } else {
          const tileX = Math.floor((pointer.x - mx) / scale);
          const tileY = Math.floor((pointer.y - my) / scale);
          const cx = mx + (tileX + 0.5) * scale;
          const cy = my + (tileY + 0.5) * scale;
          this.placementHighlight.setVisible(true);
          this.placementHighlight.setPosition(cx, cy);
          this.placementHighlight.setSize(scale, scale);
        }
      }

      if (!this.isDragging) return;
      // Pan 1:1 en screen pixels. Mueve el mapa ajustando offset.
      this.offsetX = this.dragStartOffsetX + (pointer.x - this.dragStartPointerX);
      this.offsetY = this.dragStartOffsetY + (pointer.y - this.dragStartPointerY);
      this.applyTransform();
    });

    this.bg.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = false;
      pointer.event.stopPropagation();
    });
    // Por si el cursor sale del overlay durante el drag (release fuera de browser, etc.)
    this.bg.on('pointerupoutside', () => {
      this.isDragging = false;
    });
    // Nota: wheel se registra en show() vía scene.input.on para garantizar
    // la firma correcta de 4 args (pointer, gameObjects, dx, dy).

    if (!this.cachedTextureKey) return;

    // Crear mapImage en (0,0); applyTransform() lo posiciona y escala.
    this.mapImage = this.scene.add
      .image(0, 0, this.cachedTextureKey)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(this.depth + 1);

    // Rectángulo del rango de visión actual. Stroke celeste + fill apenas perceptible.
    // Posicionado por refreshVisionRect() (centrado en el player tile).
    this.visionRect = this.scene.add
      .rectangle(0, 0, 10, 10, COLOR_VISION, 0.10)
      .setStrokeStyle(1, COLOR_VISION, 0.9)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(this.depth + 2);

    // Player marker = mini render del personaje (body + head + equipo).
    // Container que se posiciona y escala con el zoom del mapa.
    // refreshPlayerMarker() actualiza textures + position cada frame.
    this.playerContainer = this.scene.add
      .container(0, 0)
      .setScrollFactor(0)
      .setDepth(this.depth + 3);

    // Hint abajo: cómo cerrar + controles.
    this.hint = this.scene.add
      .text(screenW / 2, screenH - 14, '[M] o [ESC] cerrar  ·  rueda: zoom  ·  click rueda: reset', withGameFont({
        fontSize: '20px',
        color: '#bbbbbb',
      }))
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(this.depth + 3);
  }

  /**
   * Crea un Phaser.Image por cada tile con sprite definido en el mapa.
   * Cada sprite usa la textura REAL del tile (precargada por GameScene.preloadMapAssets).
   * Se posicionan/escalan en refreshDecorationSprites() según el zoom actual del mapa.
   */
  private buildDecorationSprites(): void {
    this.destroyDecorationSprites();
    for (const layer of this.mapData.layers) {
      // MISMO anclaje que el juego (MapTileRenderer): las capas de suelo se centran en el
      // tile (origin 0.5,0.5); las decos/techos anclan abajo-centro (origin 0.5,1) y crecen
      // hacia el norte. Con el origin (0,0) viejo, los techos (sprites altos) quedaban
      // corridos hacia abajo/derecha = "desfasados" respecto del juego.
      const isGroundLike =
        layer.name === 'ground' ||
        layer.name === 'ground_deco_under' ||
        layer.name === 'layer1_ground' ||
        layer.name === 'layer2_floor_objects';
      for (const tile of layer.tiles) {
        if (!tile.sprite) continue;
        // Resolver textura (mismo patrón que GameScene.loadAndCreateMap):
        // primero intentar la key directa, luego la surfaceKey indexada.
        let textureKey = tile.sprite;
        const surfaceKey = getSurfaceTextureKey(tile.sprite);
        if (this.scene.textures.exists(surfaceKey)) {
          textureKey = surfaceKey;
        }
        if (!this.scene.textures.exists(textureKey)) continue;

        // Ancla en px NATIVOS del mundo, idéntica al juego: X centrada en el bloque
        // (tilesX del surface, 1 si no es surface); Y = centro del bloque si es suelo, o
        // borde inferior del tile (ty+1) si es deco/techo.
        const surfaceDef = getSurfaceById(tile.sprite);
        const tilesX = surfaceDef?.tilesX ?? 1;
        const tilesY = surfaceDef?.tilesY ?? 1;
        const anchorX = tile.x * TILE_SIZE + (tilesX * TILE_SIZE) / 2;
        const anchorY = isGroundLike
          ? tile.y * TILE_SIZE + (tilesY * TILE_SIZE) / 2
          : tile.y * TILE_SIZE + TILE_SIZE;

        const img = this.scene.add
          .image(0, 0, textureKey)
          .setOrigin(0.5, isGroundLike ? 0.5 : 1)
          .setScrollFactor(0)
          .setDepth(this.depth + 1.5); // entre mapImage (1) y visionRect (2)
        img.setData('anchorX', anchorX);
        img.setData('anchorY', anchorY);
        this.decorationSprites.push(img);
      }
    }
  }

  private destroyDecorationSprites(): void {
    for (const s of this.decorationSprites) s.destroy();
    this.decorationSprites = [];
  }

  // === MARKERS =========================================================

  private loadMarkersForCharacter(): void {
    const charId = this.getCharacterId();
    if (!charId) { this.markers = []; return; }
    this.markers = loadMarkers(charId);
  }

  private persistMarkers(): void {
    const charId = this.getCharacterId();
    if (!charId) return;
    saveMarkers(charId, this.markers);
  }

  private buildMarkerDots(): void {
    this.destroyMarkerDots();
    for (const m of this.markers) {
      const dot = this.scene.add
        .rectangle(0, 0, 1, 1, m.color, 1)
        .setOrigin(0.5)
        .setStrokeStyle(1, 0x000000, 0.8)
        .setScrollFactor(0)
        .setDepth(this.depth + 2.5);
      const label = this.scene.add
        .text(0, 0, m.label, withGameFont({
          fontSize: '10px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 3,
        }))
        .setOrigin(0.5, 1)
        .setScrollFactor(0)
        .setDepth(this.depth + 2.7);
      this.markerDots.set(m.id, dot);
      this.markerLabels.set(m.id, label);
    }
  }

  private destroyMarkerDots(): void {
    for (const dot of this.markerDots.values()) dot.destroy();
    for (const lbl of this.markerLabels.values()) lbl.destroy();
    this.markerDots.clear();
    this.markerLabels.clear();
  }

  private refreshMarkerDots(): void {
    if (!this.mapImage || this.markerDots.size === 0) return;
    const scale = this.screenPxPerTile();
    const mapX = this.mapImage.x;
    const mapY = this.mapImage.y;
    for (const m of this.markers) {
      const dot = this.markerDots.get(m.id);
      const lbl = this.markerLabels.get(m.id);
      if (!dot || !lbl) continue;
      const cx = mapX + (m.tileX + 0.5) * scale;
      const cy = mapY + (m.tileY + 0.5) * scale;
      const side = Math.max(3, scale);
      dot.setPosition(cx, cy).setSize(side, side);
      lbl.setPosition(cx, cy - side / 2 - 2);
    }
  }

  // === MARKER CRUD =====================================================

  private enterPlacingMode(): void {
    this.placingMarker = true;
    this.scene.input.setDefaultCursor('crosshair');
    // Highlight del tile bajo el cursor (invisible hasta que el mouse entre al mapa).
    this.placementHighlight?.destroy();
    this.placementHighlight = this.scene.add
      .rectangle(0, 0, 1, 1, DEFAULT_MARKER_COLOR, 0.35)
      .setOrigin(0.5)
      .setStrokeStyle(2, DEFAULT_MARKER_COLOR, 0.95)
      .setScrollFactor(0)
      .setDepth(this.depth + 2.6)
      .setVisible(false);
  }

  private exitPlacingMode(): void {
    this.placingMarker = false;
    this.scene.input.setDefaultCursor('default');
    this.placementHighlight?.destroy();
    this.placementHighlight = null;
  }

  private createMarkerAt(tileX: number, tileY: number): void {
    if (tileX < 0 || tileX >= WORLD_TILES || tileY < 0 || tileY >= WORLD_TILES) return;
    showPromptModal({
      title: 'Crear marcador',
      initialValue: `Marcador ${this.markers.length + 1}`,
      placeholder: 'Nombre del marcador',
      maxLength: 40,
      onConfirm: (label) => {
        const trimmed = label.trim();
        if (trimmed.length === 0) return;
        const m: MapMarker = {
          id: newMarkerId(),
          mapId: this.mapData.id,
          tileX,
          tileY,
          label: trimmed.slice(0, 40),
          color: DEFAULT_MARKER_COLOR,
          createdAt: Date.now(),
        };
        this.markers.push(m);
        this.persistMarkers();
        this.addMarkerDot(m);
        this.dock?.refreshMarkers();
        this.applyTransform();
      },
    });
  }

  private addMarkerDot(m: MapMarker): void {
    const dot = this.scene.add
      .rectangle(0, 0, 1, 1, m.color, 1)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x000000, 0.8)
      .setScrollFactor(0)
      .setDepth(this.depth + 2.5);
    const label = this.scene.add
      .text(0, 0, m.label, withGameFont({
        fontSize: '20px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      }))
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(this.depth + 2.7);
    this.markerDots.set(m.id, dot);
    this.markerLabels.set(m.id, label);
  }

  private renameMarker(id: string): void {
    const m = this.markers.find((x) => x.id === id);
    if (!m) return;
    showPromptModal({
      title: 'Editar marcador',
      initialValue: m.label,
      placeholder: 'Nuevo nombre',
      maxLength: 40,
      onConfirm: (newName) => {
        const trimmed = newName.trim();
        if (trimmed.length === 0) return;
        m.label = trimmed.slice(0, 40);
        this.persistMarkers();
        this.markerLabels.get(m.id)?.setText(m.label);
        this.dock?.refreshMarkers();
      },
    });
  }

  private deleteMarker(id: string): void {
    const idx = this.markers.findIndex((m) => m.id === id);
    if (idx === -1) return;
    this.markers.splice(idx, 1);
    this.persistMarkers();
    this.markerDots.get(id)?.destroy();
    this.markerLabels.get(id)?.destroy();
    this.markerDots.delete(id);
    this.markerLabels.delete(id);
    this.dock?.refreshMarkers();
  }

  /**
   * Centra el mapa sobre el marker dado al MAX_ZOOM. Snap directo (sin tween).
   */
  private flyToMarker(m: MapMarker): void {
    if (!this.mapImage) return;
    const { width: screenW, height: screenH } = this.scene.scale;
    this.zoom = MAX_ZOOM;
    const scale = this.getBaseScale() * this.zoom;
    const mapW = WORLD_TILES * scale;
    const mapH = WORLD_TILES * scale;
    const baseMapX = (screenW - mapW) / 2;
    const baseMapY = (screenH - mapH) / 2;
    const desiredMapX = screenW / 2 - (m.tileX + 0.5) * scale;
    const desiredMapY = screenH / 2 - (m.tileY + 0.5) * scale;
    this.offsetX = desiredMapX - baseMapX;
    this.offsetY = desiredMapY - baseMapY;
    this.applyTransform();
  }

  private refreshDecorationSprites(): void {
    if (!this.mapImage || this.decorationSprites.length === 0) return;
    const scale = this.screenPxPerTile();
    const mapX = this.mapImage.x;
    const mapY = this.mapImage.y;
    // Factor = (px en pantalla por tile) / (TILE_SIZE px nativos por tile). El ancla está en
    // px nativos del mundo; con el origin ya seteado en build (0.5,0.5 suelo / 0.5,1 deco),
    // basta posicionar el ancla escalada y escalar el sprite por el mismo factor.
    const factor = scale / TILE_SIZE;
    for (const sprite of this.decorationSprites) {
      const anchorX = sprite.getData('anchorX') as number;
      const anchorY = sprite.getData('anchorY') as number;
      sprite.setPosition(mapX + anchorX * factor, mapY + anchorY * factor);
      sprite.setScale(factor);
    }
  }

  private refreshPlayerMarker(): void {
    if (!this.playerContainer || !this.mapImage) return;
    const tile = this.getLocalPlayerTile();
    const visual = this.getLocalPlayerVisual();
    if (!tile || !visual) {
      this.playerContainer.setVisible(false);
      return;
    }
    this.playerContainer.setVisible(true);

    // Si el visual cambió (equipo / dirección / body), re-aplicar textures.
    if (this.visualChanged(visual)) {
      this.applyVisualToSprites(visual);
      this.lastPlayerVisual = { ...visual };
    }

    // Posicionar container centrado en el tile del player.
    const scale = this.screenPxPerTile();
    const cx = this.mapImage.x + (tile.x + 0.5) * scale;
    const cy = this.mapImage.y + (tile.y + 0.5) * scale;
    this.playerContainer.setPosition(cx, cy);
    // Escalar el container: 1 tile = scale px, body native width = 32.
    // Mínimo de 0.6 para que el personaje sea visible aún a zoom bajo.
    const factor = Math.max(0.6, scale / TILE_SIZE);
    this.playerContainer.setScale(factor);
  }

  private visualChanged(v: LocalPlayerVisual): boolean {
    const old = this.lastPlayerVisual;
    if (!old) return true;
    return v.bodyId !== old.bodyId
      || v.headId !== old.headId
      || (v.helmId ?? 0) !== (old.helmId ?? 0)
      || (v.weaponId ?? 0) !== (old.weaponId ?? 0)
      || (v.shieldId ?? 0) !== (old.shieldId ?? 0)
      || (v.bootId ?? 0) !== (old.bootId ?? 0)
      || v.direction !== old.direction;
  }

  /**
   * Crea o actualiza los sprites del player container según el visual actual.
   * Body es siempre visible; head/equipo opcionales según IDs > 0.
   * Layout: origen del container = pies del personaje (centro).
   * Body crece hacia arriba; head encima del body.
   */
  private applyVisualToSprites(v: LocalPlayerVisual): void {
    if (!this.playerContainer) return;
    const scene = this.scene;

    // Body (siempre).
    const bodyKey = getBodySpriteKey(v.bodyId);
    if (scene.textures.exists(bodyKey)) {
      const bodyFrame = getBodyIdleFrame(v.direction, v.bodyId);
      if (!this.playerBody) {
        this.playerBody = scene.add.sprite(0, 0, bodyKey, bodyFrame).setOrigin(0.5, 1);
        this.playerContainer.add(this.playerBody);
      } else {
        this.playerBody.setTexture(bodyKey, bodyFrame);
      }
      this.playerBody.setVisible(true);
    } else if (this.playerBody) {
      this.playerBody.setVisible(false);
    }

    // Head: centrada sobre los pies con el MISMO anclaje que el juego (origin
    // 0.5,0.5 en HEAD_CENTER_Y), para que solape al body en vez de flotar arriba.
    if (v.headId > 0) {
      const headKey = getHeadSpriteKey(v.headId);
      if (scene.textures.exists(headKey)) {
        const headFrame = getHeadFrameForDirection(v.direction);
        if (!this.playerHead) {
          this.playerHead = scene.add.sprite(0, HEAD_CENTER_Y, headKey, headFrame).setOrigin(0.5, 0.5);
          this.playerContainer.add(this.playerHead);
        } else {
          this.playerHead.setTexture(headKey, headFrame);
          this.playerHead.setPosition(0, HEAD_CENTER_Y);
        }
        this.playerHead.setVisible(true);
      } else if (this.playerHead) {
        this.playerHead.setVisible(false);
      }
    } else if (this.playerHead) {
      this.playerHead.setVisible(false);
    }

    // Equipo: helm (sobre la cabeza, origin 0.5,0.5 en HELM_CENTER_Y), weapon
    // (al costado), shield, boots. Render simplificado para identificar al char.
    this.applyEquipSprite('playerHelm', v.helmId, `helmet${v.helmId ?? 0}`, 0, HELM_CENTER_Y, 0.5);
    this.applyEquipSprite('playerWeapon', v.weaponId, `weapon${v.weaponId ?? 0}`, 8, 0);
    this.applyEquipSprite('playerShield', v.shieldId, `shield${v.shieldId ?? 0}`, -8, 0);
    this.applyEquipSprite('playerBoot', v.bootId, `boot${v.bootId ?? 0}`, 0, 0);
  }

  private applyEquipSprite(
    field: 'playerHelm' | 'playerWeapon' | 'playerShield' | 'playerBoot',
    id: number | undefined,
    textureKey: string,
    offsetX: number,
    offsetY: number,
    originY: number = 1,
  ): void {
    if (!this.playerContainer) return;
    const has = (id ?? 0) > 0 && this.scene.textures.exists(textureKey);
    if (!has) {
      // Ocultar si existía.
      const existing = this[field];
      if (existing) existing.setVisible(false);
      return;
    }
    let sprite = this[field];
    if (!sprite) {
      sprite = this.scene.add.sprite(offsetX, offsetY, textureKey).setOrigin(0.5, originY);
      this.playerContainer.add(sprite);
      this[field] = sprite;
    } else {
      sprite.setTexture(textureKey);
      sprite.setPosition(offsetX, offsetY);
    }
    sprite.setVisible(true);
  }

  /**
   * Calcula el baseScale (zoom=1 = fit-to-screen 85%) actual.
   */
  private getBaseScale(): number {
    const { width: screenW, height: screenH } = this.scene.scale;
    return (Math.min(screenW, screenH) * 0.85) / WORLD_TILES;
  }

  /**
   * Px de PANTALLA por tile al zoom actual. Es la unidad que usan todos los
   * cálculos tile↔pantalla (independiente de la resolución de la textura).
   */
  private screenPxPerTile(): number {
    return this.getBaseScale() * this.zoom;
  }

  /**
   * Aplica el transform actual (zoom + offset) al mapImage, y refresca todo
   * lo que depende de la escala del mapa: player marker + vision rect.
   */
  private applyTransform(): void {
    if (!this.mapImage) return;
    const { width: screenW, height: screenH } = this.scene.scale;
    const scale = this.getBaseScale() * this.zoom;
    const mapW = WORLD_TILES * scale;
    const mapH = WORLD_TILES * scale;
    const mapX = (screenW - mapW) / 2 + this.offsetX;
    const mapY = (screenH - mapH) / 2 + this.offsetY;
    // La textura mide texSize px (= WORLD_TILES * pxPerTile); para que en pantalla
    // mida WORLD_TILES * scale, la imagen se escala por scale / pxPerTile.
    this.mapImage.setPosition(mapX, mapY).setScale(scale / this.pxPerTile);
    this.refreshDecorationSprites();
    this.refreshMarkerDots();
    this.refreshPlayerMarker();
    this.refreshVisionRect();
    this.refreshWorldMapLayers();
  }

  /**
   * Wheel handler: zoom anclado al cursor (el tile bajo el cursor queda fijo).
   * dy < 0 = scroll up = zoom in.
   */
  private handleWheelZoom(pointer: Phaser.Input.Pointer, dy: number): void {
    if (!this.mapImage) return;
    const { width: screenW, height: screenH } = this.scene.scale;
    const baseScale = this.getBaseScale();
    const oldScale = baseScale * this.zoom;
    const newZoom = Phaser.Math.Clamp(
      this.zoom * (dy < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR),
      MIN_ZOOM,
      MAX_ZOOM,
    );
    if (newZoom === this.zoom) return;
    const newScale = baseScale * newZoom;
    // Tile (en coords del mapa) que estaba bajo el cursor antes del zoom.
    const tileX = (pointer.x - this.mapImage.x) / oldScale;
    const tileY = (pointer.y - this.mapImage.y) / oldScale;
    this.zoom = newZoom;
    // Calcular el offset necesario para que ese tile siga bajo el cursor.
    const desiredMapX = pointer.x - tileX * newScale;
    const desiredMapY = pointer.y - tileY * newScale;
    const newMapW = WORLD_TILES * newScale;
    const newMapH = WORLD_TILES * newScale;
    const baseMapX = (screenW - newMapW) / 2;
    const baseMapY = (screenH - newMapH) / 2;
    this.offsetX = desiredMapX - baseMapX;
    this.offsetY = desiredMapY - baseMapY;
    this.applyTransform();
  }

  /**
   * Renderiza el rectángulo del rango de visión actual centrado en el player tile.
   * Ancho/alto: (2*VIEW_HALF_RANGE_X+1) × (2*VIEW_HALF_RANGE_Y+1) tiles × escala.
   */
  private refreshVisionRect(): void {
    if (!this.visionRect || !this.mapImage) return;
    const tile = this.getLocalPlayerTile();
    if (!tile) {
      this.visionRect.setVisible(false);
      return;
    }
    const scale = this.screenPxPerTile();
    const widthPx = (2 * VIEW_HALF_RANGE_X + 1) * scale;
    const heightPx = (2 * VIEW_HALF_RANGE_Y + 1) * scale;
    const centerX = this.mapImage.x + (tile.x + 0.5) * scale;
    const centerY = this.mapImage.y + (tile.y + 0.5) * scale;
    this.visionRect.setSize(widthPx, heightPx);
    this.visionRect.setPosition(centerX, centerY);
    this.visionRect.setVisible(true);
  }

  // === WORLD MAP INTERACTIVO ================================================

  /** Tile (x,y) → coordenada de pantalla del centro del tile. */
  private tileToScreen(tileX: number, tileY: number): { x: number; y: number } {
    const scale = this.screenPxPerTile();
    return {
      x: (this.mapImage?.x ?? 0) + (tileX + 0.5) * scale,
      y: (this.mapImage?.y ?? 0) + (tileY + 0.5) * scale,
    };
  }

  /** Crea (una vez, al abrir) los objetos de las capas del World Map. */
  private buildWorldMapLayers(): void {
    this.destroyWorldMapLayers();

    // Overlay de áreas (rects + labels).
    this.areaGfx = this.scene.add.graphics().setScrollFactor(0).setDepth(this.depth + 1.3);
    for (const a of this.wmIndex.areas) {
      const lbl = this.scene.add
        .text(0, 0, a.name, withGameFont({
          fontSize: '22px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 3,
        }))
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(this.depth + 1.35);
      lbl.setData('areaId', a.id);
      this.areaLabels.push(lbl);
    }

    // Pins de NPCs neutrales (uno por placement).
    for (const npc of this.wmIndex.neutrals) {
      const color = npc.role === 'merchant' || npc.role === 'spellVendor'
        ? 0x7ad07a
        : npc.role === 'questGiver'
          ? 0xc79bff
          : 0xffd94a;
      const dot = this.scene.add
        .circle(0, 0, 4, color, 1)
        .setStrokeStyle(1, 0x000000, 0.8)
        .setScrollFactor(0)
        .setDepth(this.depth + 2.2)
        .setInteractive({ useHandCursor: true });
      dot.on('pointerover', () => dot.setStrokeStyle(2, 0xffffff, 1));
      dot.on('pointerout', () => dot.setStrokeStyle(1, 0x000000, 0.8));
      dot.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.showNpcTemplate(npc.templateId, dot.x, dot.y);
        p.event.stopPropagation();
      });
      this.neutralPins.push({ npc, dot });
    }

    // Badges de hostiles: por área con nombre + clusters de mundo abierto.
    for (const a of this.wmIndex.areas) {
      const info = this.wmIndex.areaNpcs.get(a.id);
      if (!info || info.hostileTotal <= 0) continue;
      this.hostileBadges.push(this.makeHostileBadge(a.center.x, a.center.y, info.hostileTotal, info.hostiles));
    }
    for (const cluster of this.buildOpenHostileClusters()) {
      this.hostileBadges.push(this.makeHostileBadge(cluster.x, cluster.y, cluster.total, cluster.breakdown));
    }

    // Estrellas de boss (hostiles isBoss) en su placement.
    for (const npc of this.wmIndex.hostiles) {
      if (!npc.isBoss) continue;
      const star = this.scene.add
        .text(0, 0, '★', withGameFont({ fontSize: '32px', color: '#ffd56b', stroke: '#000000', strokeThickness: 3 }))
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(this.depth + 2.4)
        .setInteractive({ useHandCursor: true });
      star.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.showNpcTemplate(npc.templateId, star.x, star.y);
        p.event.stopPropagation();
      });
      this.bossPins.push({ npc, star });
    }

    // Pins de yacimientos (minería naranja / leñería verde), forma de rombo.
    for (const node of this.wmIndex.resourceNodes) {
      const color = node.skill === 'skill_mining' ? 0xe0a13c : 0x6fae5a;
      const marker = this.scene.add
        .rectangle(0, 0, 7, 7, color, 1)
        .setStrokeStyle(1, 0x000000, 0.85)
        .setAngle(45)
        .setScrollFactor(0)
        .setDepth(this.depth + 2.35)
        .setInteractive({ useHandCursor: true });
      marker.on('pointerover', () => marker.setStrokeStyle(2, 0xffffff, 1));
      marker.on('pointerout', () => marker.setStrokeStyle(1, 0x000000, 0.85));
      marker.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.showResourceInfo(node, marker.x, marker.y);
        p.event.stopPropagation();
      });
      this.resourcePins.push({ node, marker });
    }

    // Ruta de quest.
    this.routeGfx = this.scene.add.graphics().setScrollFactor(0).setDepth(this.depth + 2.6);
    this.routeDest = this.scene.add
      .circle(0, 0, 8, 0xffd56b, 0.25)
      .setStrokeStyle(2, 0xffd56b, 1)
      .setScrollFactor(0)
      .setDepth(this.depth + 2.7)
      .setVisible(false);
    this.routeLabel = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '22px', color: '#ffe9a8', stroke: '#000000', strokeThickness: 3 }))
      .setOrigin(0.5, 1)
      .setScrollFactor(0)
      .setDepth(this.depth + 2.7)
      .setVisible(false);

    // Tarjeta de NPC (por encima de todo lo del mapa).
    this.npcCard = new NpcDetailCard(this.scene, this.depth + 5);
  }

  /** Agrupa hostiles fuera de toda área con nombre en clusters por celda gruesa. */
  private buildOpenHostileClusters(): Array<{ x: number; y: number; total: number; breakdown: HostileCount[] }> {
    const CELL = 24;
    const inNamedArea = (x: number, y: number): boolean =>
      this.wmIndex.areas.some((a) => a.rects.some((r) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h));
    const cells = new Map<string, { sx: number; sy: number; n: number; per: Map<string, HostileCount> }>();
    for (const npc of this.wmIndex.hostiles) {
      if (inNamedArea(npc.x, npc.y)) continue;
      const cx = Math.floor(npc.x / CELL);
      const cy = Math.floor(npc.y / CELL);
      const key = `${cx},${cy}`;
      let cell = cells.get(key);
      if (!cell) {
        cell = { sx: 0, sy: 0, n: 0, per: new Map() };
        cells.set(key, cell);
      }
      cell.sx += npc.x;
      cell.sy += npc.y;
      cell.n += 1;
      const existing = cell.per.get(npc.templateId);
      if (existing) existing.count += 1;
      else cell.per.set(npc.templateId, { templateId: npc.templateId, name: npc.name, isBoss: npc.isBoss, count: 1 });
    }
    const out: Array<{ x: number; y: number; total: number; breakdown: HostileCount[] }> = [];
    for (const cell of cells.values()) {
      out.push({
        x: Math.round(cell.sx / cell.n),
        y: Math.round(cell.sy / cell.n),
        total: cell.n,
        breakdown: Array.from(cell.per.values()).sort((a, b) => b.count - a.count),
      });
    }
    return out;
  }

  /** Crea el container de un badge de hostiles (☠ N) clickeable → popover. */
  private makeHostileBadge(
    tileX: number,
    tileY: number,
    total: number,
    breakdown: HostileCount[]
  ): { tileX: number; tileY: number; total: number; breakdown: HostileCount[]; cont: Phaser.GameObjects.Container } {
    const cont = this.scene.add.container(0, 0).setScrollFactor(0).setDepth(this.depth + 2.3);
    const label = `☠ ${total}`;
    const txt = this.scene.add
      .text(0, 0, label, withGameFont({ fontSize: '24px', color: '#ffd0cc', fontStyle: 'bold' }))
      .setOrigin(0.5)
      .setScrollFactor(0);
    const padX = 6;
    const bg = this.scene.add
      .rectangle(0, 0, txt.width + padX * 2, txt.height + 4, 0x3a1212, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xd6564a, 0.9)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setStrokeStyle(2, 0xffffff, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(1, 0xd6564a, 0.9));
    bg.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.showHostilePopover(breakdown, cont.x, cont.y);
      p.event.stopPropagation();
    });
    cont.add([bg, txt]);
    return { tileX, tileY, total, breakdown, cont };
  }

  /** Popover con el desglose por template de una zona hostil. */
  private showHostilePopover(breakdown: HostileCount[], screenX: number, screenY: number): void {
    this.closeHostilePopover();
    // Tipografía al DOBLE (12→24) → fila más alta y popover más ancho para que no se corte.
    const rowH = 40;
    const W = 380;
    const H = 20 + breakdown.length * rowH;
    const { width: sw, height: sh } = this.scene.scale;
    const px = Phaser.Math.Clamp(screenX + 12, 8, sw - W - 8);
    const py = Phaser.Math.Clamp(screenY - H / 2, 8, sh - H - 8);
    const cont = this.scene.add.container(px, py).setScrollFactor(0).setDepth(this.depth + 4.5);
    const bg = this.scene.add
      .rectangle(0, 0, W, H, 0x14130f, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0x8a7a4a, 1)
      .setScrollFactor(0)
      .setInteractive();
    bg.on('pointerdown', (p: Phaser.Input.Pointer) => p.event.stopPropagation());
    cont.add(bg);
    breakdown.forEach((h, i) => {
      const y = 10 + i * rowH + rowH / 2;
      const row = this.scene.add
        .text(16, y, `${h.name}${h.isBoss ? ' ★' : ''}  ×${h.count}`, withGameFont({
          fontSize: '24px',
          color: h.isBoss ? '#ffd56b' : '#e8e0cc',
        }))
        .setOrigin(0, 0.5)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      row.on('pointerover', () => row.setColor('#ffffff'));
      row.on('pointerout', () => row.setColor(h.isBoss ? '#ffd56b' : '#e8e0cc'));
      row.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.showNpcTemplate(h.templateId, screenX, screenY);
        this.closeHostilePopover();
        p.event.stopPropagation();
      });
      cont.add(row);
    });
    this.hostilePopover = cont;
  }

  private closeHostilePopover(): void {
    this.hostilePopover?.destroy(true);
    this.hostilePopover = null;
  }

  private buildDock(): void {
    this.dock = new WorldMapDock(
      {
        onFlyToTile: (x, y) => this.flyToTile(x, y),
        onSelectArea: (areaId) => this.selectArea(areaId),
        onTrackQuest: (questId) => this.trackQuest(questId),
        onShowNpcTemplate: (templateId) => {
          const c = this.tileToScreenForTemplate(templateId);
          this.showNpcTemplate(templateId, c?.x ?? this.scene.scale.width / 2, c?.y ?? this.scene.scale.height / 2);
        },
        onFiltersChange: (f) => this.setFilters(f),
        onCreateMarker: () => this.enterPlacingMode(),
        onFlyToMarker: (id) => {
          const m = this.markers.find((x) => x.id === id);
          if (m) this.flyToMarker(m);
        },
        onRenameMarker: (id) => this.renameMarker(id),
        onDeleteMarker: (id) => this.deleteMarker(id),
      },
      {
        index: this.wmIndex,
        getActiveQuests: () => this.getActiveQuests?.() ?? [],
        getCompletedQuestIds: () => this.getCompletedQuestIds?.() ?? new Set<string>(),
        getPlayerLevel: () => this.getPlayerLevel?.() ?? 1,
        getMarkers: () => this.markers,
        initialTrackedQuestId: this.trackedQuestId,
      }
    );
    this.filters = this.dock.getFilters();
    this.dock.show();
  }

  /** Primer placement conocido de un template → coord de pantalla (para anclar la tarjeta). */
  private tileToScreenForTemplate(templateId: string): { x: number; y: number } | null {
    const list = this.wmIndex.npcsByTemplate.get(templateId);
    if (!list || list.length === 0) return null;
    return this.tileToScreen(list[0].x, list[0].y);
  }

  private destroyWorldMapLayers(): void {
    this.dock?.hide();
    this.dock = null;
    this.npcCard?.hide();
    this.npcCard = null;
    this.closeHostilePopover();
    this.areaGfx?.destroy();
    this.areaGfx = null;
    for (const l of this.areaLabels) l.destroy();
    this.areaLabels = [];
    for (const p of this.neutralPins) p.dot.destroy();
    this.neutralPins = [];
    for (const b of this.hostileBadges) b.cont.destroy(true);
    this.hostileBadges = [];
    for (const b of this.bossPins) b.star.destroy();
    this.bossPins = [];
    for (const r of this.resourcePins) r.marker.destroy();
    this.resourcePins = [];
    this.routeGfx?.destroy();
    this.routeGfx = null;
    this.routeDest?.destroy();
    this.routeDest = null;
    this.routeLabel?.destroy();
    this.routeLabel = null;
    this.routeTiles = null;
    this.routeGoal = null;
    this.routeSig = '';
    this.trackedQuestId = null;
  }

  /** Reposiciona/culea todas las capas del World Map según el transform actual. */
  private refreshWorldMapLayers(): void {
    if (!this.mapImage) return;
    this.refreshAreaOverlay();
    this.refreshNpcPins();
    this.refreshHostileBadges();
    this.refreshBossPins();
    this.refreshResourcePins();
    this.maybeRecomputeRoute();
    this.refreshRoute();
  }

  private refreshAreaOverlay(): void {
    if (!this.areaGfx || !this.mapImage) return;
    const g = this.areaGfx;
    g.clear();
    const scale = this.screenPxPerTile();
    const mapX = this.mapImage.x;
    const mapY = this.mapImage.y;
    const show = this.filters.areas;
    for (const a of this.wmIndex.areas) {
      const fill = ZONE_FILL[a.zoneType] ?? 0x888888;
      const isHi = a.id === this.highlightedAreaId;
      if (show) {
        g.fillStyle(fill, isHi ? 0.28 : 0.13);
        g.lineStyle(isHi ? 2 : 1, fill, isHi ? 1 : 0.6);
        for (const r of a.rects) {
          const x = mapX + r.x * scale;
          const y = mapY + r.y * scale;
          g.fillRect(x, y, r.w * scale, r.h * scale);
          g.strokeRect(x, y, r.w * scale, r.h * scale);
        }
      }
    }
    // Labels de área.
    for (const lbl of this.areaLabels) {
      const areaId = lbl.getData('areaId') as string;
      const a = this.wmIndex.areas.find((x) => x.id === areaId);
      if (!a || !show) {
        lbl.setVisible(false);
        continue;
      }
      const c = this.tileToScreen(a.center.x, a.center.y);
      lbl.setPosition(c.x, c.y).setVisible(this.inViewport(c.x, c.y));
    }
  }

  private refreshNpcPins(): void {
    if (!this.mapImage) return;
    const scale = this.screenPxPerTile();
    const showPins = this.zoom >= NPC_PIN_ZOOM;
    for (const { npc, dot } of this.neutralPins) {
      const isVendor = npc.role === 'merchant' || npc.role === 'spellVendor';
      const filterOn = isVendor ? this.filters.vendors : this.filters.neutrals;
      const c = this.tileToScreen(npc.x, npc.y);
      const visible = filterOn && showPins && this.inViewport(c.x, c.y);
      dot.setVisible(visible);
      if (visible) {
        dot.setPosition(c.x, c.y);
        dot.setRadius(Math.max(3, Math.min(6, scale * 0.35)));
      }
    }
  }

  private refreshHostileBadges(): void {
    if (!this.mapImage) return;
    const show = this.filters.hostiles;
    for (const b of this.hostileBadges) {
      const c = this.tileToScreen(b.tileX, b.tileY);
      const visible = show && this.inViewport(c.x, c.y);
      b.cont.setVisible(visible);
      if (visible) b.cont.setPosition(c.x, c.y);
    }
  }

  private refreshBossPins(): void {
    if (!this.mapImage) return;
    const show = this.filters.bosses;
    for (const { npc, star } of this.bossPins) {
      const c = this.tileToScreen(npc.x, npc.y);
      const visible = show && this.inViewport(c.x, c.y);
      star.setVisible(visible);
      if (visible) star.setPosition(c.x, c.y);
    }
  }

  private refreshResourcePins(): void {
    if (!this.mapImage) return;
    const showPins = this.filters.resources && this.zoom >= NPC_PIN_ZOOM;
    for (const { node, marker } of this.resourcePins) {
      const c = this.tileToScreen(node.x, node.y);
      const visible = showPins && this.inViewport(c.x, c.y);
      marker.setVisible(visible);
      if (visible) marker.setPosition(c.x, c.y);
    }
  }

  /** Info compacta de un yacimiento (nombre + skill/nivel + material que da). */
  private showResourceInfo(node: WorldMapResourceNode, screenX: number, screenY: number): void {
    this.closeHostilePopover();
    const skillLabel = node.skill === 'skill_mining' ? 'Minería' : 'Leñería';
    const material = getItemTemplate(node.producesItemId)?.name ?? node.producesItemId;
    const lines = [node.name, `${skillLabel} · nivel ${node.requiredLevel}`, `Da: ${material}`];
    // Tipografía al DOBLE (13/11 → 26/22): popover más ancho y filas más altas.
    const W = 380;
    const ROW_H = 34;
    const H = 20 + lines.length * ROW_H;
    const { width: sw, height: sh } = this.scene.scale;
    const px = Phaser.Math.Clamp(screenX + 12, 8, sw - W - 8);
    const py = Phaser.Math.Clamp(screenY - H / 2, 8, sh - H - 8);
    const cont = this.scene.add.container(px, py).setScrollFactor(0).setDepth(this.depth + 4.5);
    const bg = this.scene.add
      .rectangle(0, 0, W, H, 0x14130f, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(2, node.skill === 'skill_mining' ? 0xe0a13c : 0x6fae5a, 1)
      .setScrollFactor(0)
      .setInteractive();
    bg.on('pointerdown', (p: Phaser.Input.Pointer) => p.event.stopPropagation());
    cont.add(bg);
    lines.forEach((t, i) => {
      const txt = this.scene.add
        .text(14, 12 + i * ROW_H, t, withGameFont({
          fontSize: i === 0 ? '26px' : '22px',
          color: i === 0 ? '#ffe9a8' : '#e8e0cc',
          fontStyle: i === 0 ? 'bold' : 'normal',
        }))
        .setScrollFactor(0);
      cont.add(txt);
    });
    this.hostilePopover = cont;
  }

  /** True si (sx,sy) cae dentro de la ventana del mapa en pantalla. */
  private inViewport(sx: number, sy: number): boolean {
    if (!this.mapImage) return false;
    const scale = this.screenPxPerTile();
    const mx = this.mapImage.x;
    const my = this.mapImage.y;
    const mw = WORLD_TILES * scale;
    const mh = WORLD_TILES * scale;
    const M = 40;
    return sx >= mx - M && sx <= mx + mw + M && sy >= my - M && sy <= my + mh + M;
  }

  // === RUTA DE QUEST =======================================================

  private trackQuest(questId: string | null): void {
    this.trackedQuestId = questId;
    this.routeSig = ''; // fuerza recompute
    this.maybeRecomputeRoute();
    this.refreshRoute();
    // Sincronizar la favorita (fuente única en QuestUI): trackear desde el dock
    // marca la estrella de esa quest y persiste.
    this.onTrackQuestChange?.(questId);
  }

  private setFilters(f: WorldMapFilters): void {
    this.filters = f;
    this.refreshWorldMapLayers();
  }

  /** Recomputa la ruta si cambió la quest trackeada o el player se movió. */
  private maybeRecomputeRoute(): void {
    if (!this.trackedQuestId) {
      this.routeTiles = null;
      this.routeGoal = null;
      return;
    }
    const quests = this.getActiveQuests?.() ?? [];
    const quest = quests.find((q) => q.id === this.trackedQuestId);
    const playerTile = this.getLocalPlayerTile();
    if (!quest || !playerTile) {
      this.routeTiles = null;
      this.routeGoal = null;
      return;
    }
    // Primer objetivo incompleto y activo.
    const obj = quest.objectives.find((o) => !o.complete && o.active) ?? quest.objectives.find((o) => !o.complete);
    if (!obj) {
      this.routeTiles = null;
      this.routeGoal = null;
      return;
    }
    const targets = resolveObjectiveViewTargets(obj, this.wmIndex);
    const goal = nearestTarget(targets, playerTile);
    if (!goal) {
      this.routeTiles = null;
      this.routeGoal = null;
      this.routeSig = `none:${this.trackedQuestId}`;
      return;
    }
    const sig = `${this.trackedQuestId}:${goal.x},${goal.y}:${Math.floor(playerTile.x / 6)},${Math.floor(playerTile.y / 6)}`;
    if (sig === this.routeSig) return;
    this.routeSig = sig;
    this.routeGoal = goal;

    const blocked = this.getBlockedTiles?.() ?? null;
    const water = this.getWaterTiles?.() ?? null;
    const isBlocked = (x: number, y: number): boolean =>
      (blocked?.has(`${x},${y}`) ?? false) || (water?.has(`${x},${y}`) ?? false);
    const route = findCoarseRoute(playerTile, goal, { worldTiles: WORLD_TILES, isBlocked });
    // Fallback recto si no hay ruta (o presupuesto excedido).
    this.routeTiles = route ?? [ { x: playerTile.x, y: playerTile.y }, { x: goal.x, y: goal.y } ];
  }

  private refreshRoute(): void {
    if (!this.routeGfx || !this.routeDest || !this.routeLabel || !this.mapImage) return;
    const g = this.routeGfx;
    g.clear();
    if (!this.filters.quests || !this.routeTiles || this.routeTiles.length < 2 || !this.routeGoal) {
      this.routeDest.setVisible(false);
      this.routeLabel.setVisible(false);
      return;
    }
    // Breadcrumb dorado.
    g.lineStyle(3, 0xffd56b, 0.9);
    g.beginPath();
    const first = this.tileToScreen(this.routeTiles[0].x, this.routeTiles[0].y);
    g.moveTo(first.x, first.y);
    for (let i = 1; i < this.routeTiles.length; i++) {
      const s = this.tileToScreen(this.routeTiles[i].x, this.routeTiles[i].y);
      g.lineTo(s.x, s.y);
    }
    g.strokePath();

    // Marker de destino + distancia.
    const dc = this.tileToScreen(this.routeGoal.x, this.routeGoal.y);
    this.routeDest.setPosition(dc.x, dc.y).setVisible(true);
    const pt = this.getLocalPlayerTile();
    const dist = pt
      ? Math.round(Math.hypot(this.routeGoal.x - pt.x, this.routeGoal.y - pt.y))
      : 0;
    this.routeLabel
      .setText(`${this.routeGoal.label}  (${dist} tiles)`)
      .setPosition(dc.x, dc.y - 12)
      .setVisible(true);
  }

  // === API pública para el dock ============================================

  /** Centra el mapa en un tile a MAX_ZOOM (snap directo). */
  private flyToTile(tileX: number, tileY: number): void {
    if (!this.mapImage) return;
    const { width: screenW, height: screenH } = this.scene.scale;
    this.zoom = MAX_ZOOM;
    const scale = this.getBaseScale() * this.zoom;
    const mapW = WORLD_TILES * scale;
    const mapH = WORLD_TILES * scale;
    const baseMapX = (screenW - mapW) / 2;
    const baseMapY = (screenH - mapH) / 2;
    this.offsetX = (screenW / 2 - (tileX + 0.5) * scale) - baseMapX;
    this.offsetY = (screenH / 2 - (tileY + 0.5) * scale) - baseMapY;
    this.applyTransform();
  }

  private selectArea(areaId: string): void {
    const a = this.wmIndex.areas.find((x) => x.id === areaId);
    if (!a) return;
    this.highlightedAreaId = areaId;
    this.flyToTile(a.center.x, a.center.y);
  }

  /** Muestra la tarjeta de un NPC (por templateId) anclada cerca de (sx,sy). */
  private showNpcTemplate(templateId: string, sx: number, sy: number): void {
    this.npcCard?.showTemplate(templateId, sx, sy);
  }

}
